import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectProjectPermissions } from 'state/Project/permissions/projectPermissionsSlice';
import {
  selectNumberOfSelectedTasks,
  resetSelectedTasks,
} from 'state/ProjectTasksList/export/exportSlice';
import { resetAvailableAssignees } from 'state/ProjectTasksList/bulkActions/bulkAssign/availableAssignees/availableAssigneesSlice';
import { selectIsBulkOperationInProgress } from 'state/ProjectTasksList/bulkActions/bulkActionsSlice';
import { useAppThunkDispatch } from 'state/store';

interface UseBulkActionsProps {
  bulkTaskAssignFlag: boolean;
  bulkTaskDeleteFlag: boolean;
}

export const useBulkActions = ({
  bulkTaskAssignFlag,
  bulkTaskDeleteFlag,
}: UseBulkActionsProps) => {
  const dispatch = useAppThunkDispatch();
  const numberOfSelectedTasks = useSelector(selectNumberOfSelectedTasks);
  const projectPermissions = useSelector(selectProjectPermissions);
  const isBulkOperationInProgress = useSelector(
    selectIsBulkOperationInProgress
  );
  const [isBulkActionsBarVisible, setIsBulkActionsBarVisible] =
    useState<boolean>(false);

  const canBulkAssign = projectPermissions?.canBulkAssign && bulkTaskAssignFlag;
  const canBulkDelete = projectPermissions?.canBulkDelete && bulkTaskDeleteFlag;

  const hasProjectTasksSelected = numberOfSelectedTasks > 0;

  useEffect(() => {
    if (isBulkActionsBarVisible && !hasProjectTasksSelected) {
      setIsBulkActionsBarVisible(false);
    }

    if (hasProjectTasksSelected && !isBulkActionsBarVisible) {
      setIsBulkActionsBarVisible(true);
    }
  }, [hasProjectTasksSelected, isBulkActionsBarVisible]);

  const handleCloseBulkActionsBar = useCallback(() => {
    dispatch(resetSelectedTasks());
    dispatch(resetAvailableAssignees());
    setIsBulkActionsBarVisible(false);
  }, [dispatch]);

  return useMemo(
    () => ({
      canBulkAssign,
      canBulkDelete,
      handleCloseBulkActionsBar,
      isBulkActionsBarVisible,
      isBulkOperationInProgress,
    }),
    [
      handleCloseBulkActionsBar,
      isBulkActionsBarVisible,
      canBulkAssign,
      canBulkDelete,
      isBulkOperationInProgress,
    ]
  );
};
